export type AskSource = { label: string; url: string };
export type AskMessage = { role: "user" | "assistant"; content: string; sources?: AskSource[] };

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-superanalyst`;

export async function streamAskSuperAnalyst({
  messages,
  locale,
  page,
  onSources,
  onDelta,
  onDone,
  onError,
  signal,
}: {
  messages: AskMessage[];
  locale?: "india" | "international";
  /** The page the reader is on; a company page tells the assistant which company "it" is. */
  page?: string;
  /** The pages and filings the answer was built from, sent before the answer itself. */
  onSources?: (sources: AskSource[]) => void;
  onDelta: (chunk: string) => void;
  onDone: () => void;
  onError: (err: { status?: number; message: string }) => void;
  signal?: AbortSignal;
}) {
  try {
    const resp = await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ messages: messages.map(({ role, content }) => ({ role, content })), locale, page }),
      signal,
    });

    if (!resp.ok || !resp.body) {
      let message = "Something went wrong. Please try again.";
      if (resp.status === 429) message = "Too many requests. Please wait a moment and try again.";
      if (resp.status === 402) message = "AI credits exhausted. Please try again later.";
      try {
        const data = await resp.json();
        if (data?.error) message = data.error;
      } catch {}
      onError({ status: resp.status, message });
      return;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let streamDone = false;

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") {
          streamDone = true;
          break;
        }
        try {
          const parsed = JSON.parse(jsonStr);
          if (Array.isArray(parsed.sources)) onSources?.(parsed.sources);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) onDelta(content);
        } catch {
          textBuffer = line + "\n" + textBuffer;
          break;
        }
      }
    }

    if (textBuffer.trim()) {
      for (let raw of textBuffer.split("\n")) {
        if (!raw) continue;
        if (raw.endsWith("\r")) raw = raw.slice(0, -1);
        if (!raw.startsWith("data: ")) continue;
        const jsonStr = raw.slice(6).trim();
        if (jsonStr === "[DONE]") continue;
        try {
          const parsed = JSON.parse(jsonStr);
          if (Array.isArray(parsed.sources)) onSources?.(parsed.sources);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) onDelta(content);
        } catch {}
      }
    }

    onDone();
  } catch (e: any) {
    if (e?.name === "AbortError") return;
    onError({ message: e?.message || "Network error" });
  }
}
